import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');
    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, { signal });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    // Hue Skin fix: for product-page routine variants, request the product page
    // with the exact selected variant ID and take the gallery returned by Shopify.
    // This bypasses any ambiguity in option_values resolution and guarantees the
    // gallery follows the image assigned to Skin Start / Skin Focus / Skin Complete.
    const picker = event.target.closest('variant-picker');
    const variantId = event.detail?.variantId || event.detail?.detail?.variantId;
    const productUrl = picker?.dataset?.productUrl;
    const isProductPage = picker?.dataset?.templateProductMatch === 'true';

    if (isProductPage && variantId && productUrl) {
      const url = new URL(productUrl, window.location.origin);
      url.searchParams.set('variant', variantId);
      fetch(url.toString(), { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`Variant media request failed: ${response.status}`);
          return response.text();
        })
        .then((text) => {
          const html = new DOMParser().parseFromString(text, 'text/html');
          const newMediaGallery = html.querySelector('media-gallery');
          if (!newMediaGallery) throw new Error('No media-gallery returned for selected variant');
          this.replaceWith(newMediaGallery);
        })
        .catch((error) => {
          if (error?.name !== 'AbortError') console.warn('[media-gallery] Variant media request failed:', error);
        });
      return;
    }

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;
        const html = detail.html;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;
        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;
    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() { return this.refs.slideshow; }
  get media() { return this.refs.media; }
  get presentation() { return this.dataset.presentation; }
}

if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);
